import {
  ROTATION_PASTE_SKIP_ACTUAL_SOWING,
  ROTATION_PASTE_SKIP_LOCKED,
  ROTATION_PASTE_SKIP_MULTIPLE_SOWING,
  ROTATION_PASTE_SKIP_NO_ZONE,
  ROTATION_PASTE_SKIP_SECOND_ROW,
} from 'constants/messageConstants';
import {
  ROTATION_COPIED_COUNT,
  ROTATION_PASTE_MAX_CELLS,
} from 'constants/uiConstants';
import dayjs from 'dayjs';
import { InputTaskCategory } from 'interfaces/IInputTask';
import type { TipText } from 'pages/FarmsPage/Workspace/Insights/nutrition/TipContent';
import type { RotationCell, RotationPaddockRow } from './computeRotationGrid';
import type { RotationCellValues } from './useRotationCellSave';

/** A cell's place in the grid: its row (a paddock's lane) and year column. */
export interface GridPosition {
  row: number;
  col: number;
}

/** One navigable grid row — a paddock's lane. */
export interface RotationGridRow {
  paddock: RotationPaddockRow;
  lane: number;
}

/** Identifies a cell however the grid's rows move: paddock, year and lane. */
export const rotationCellKey = (
  paddockId: string,
  season: number,
  lane: number
) => `${paddockId}:${season}:${lane}`;

/** One copied crop, at its offset from the copied block's top-left corner. */
export interface RotationClipboardEntry {
  dRow: number;
  dCol: number;
  values: RotationCellValues;
}

/**
 * What Ctrl/⌘+C holds: one copied cell, or a rectangular block of them. A
 * single cell pastes into every selected cell; a block pastes with its
 * top-left corner at the top-left of the selection, cell for cell.
 */
export interface RotationClipboard {
  sourceKeys: string[];
  /** e.g. "Canola · HyTTec Trophy", or "6 crops" for a block. */
  label: string;
  width: number;
  height: number;
  /** Cells with a crop only — gaps in a block aren't pasted, so they never clear a plan. */
  entries: RotationClipboardEntry[];
}

export interface RotationPasteTarget {
  key: string;
  position: GridPosition;
  paddockId: string;
  paddockName: string;
  season: number;
  lane: number;
  /** The plan the paste updates, or null when it creates one. */
  inputId: string | null;
  hasOtherTasks: boolean;
  /** True when the plan sows a seed mix that the paste replaces with one seed. */
  replacesMix: boolean;
  /** What this cell gets — the copied cell's values. */
  values: RotationCellValues;
}

export interface RotationPasteSkip {
  key: string;
  position: GridPosition;
  paddockName: string;
  season: number;
  reason: string;
}

export interface RotationPastePlan {
  clipboard: RotationClipboard;
  targets: RotationPasteTarget[];
  skipped: RotationPasteSkip[];
  /** Copied crops that land past the last paddock or year shown. */
  outside: number;
  /** More cells can take the paste than one paste allows. */
  tooMany: boolean;
}

const toValues = (cell: RotationCell): RotationCellValues => {
  const sowing = cell.sowingState === 'single' ? cell.sowing : null;
  return {
    crop: { cropName: cell.cropName ?? '', globalId: cell.cropId },
    variety: cell.varietyName
      ? { varietyName: cell.varietyName, globalId: cell.varietyId }
      : null,
    sowing: {
      rate: sowing?.rate ?? null,
      unit: sowing?.unit ?? null,
      cost: sowing?.cost ?? null,
      date: sowing?.dateHasDay ? sowing.date : null,
      mix: sowing?.mix ?? null,
    },
  };
};

const cellAt = (
  rows: RotationGridRow[],
  seasons: number[],
  { row, col }: GridPosition
) => {
  const gridRow = rows[row];
  const season = seasons[col];
  if (!gridRow || season === null || season === undefined) return null;
  return {
    gridRow,
    season,
    key: rotationCellKey(gridRow.paddock.paddockId, season, gridRow.lane),
    cell: gridRow.paddock.cells[season]?.[gridRow.lane] ?? null,
  };
};

export type RotationCopyResult =
  | { clipboard: RotationClipboard }
  | { error: 'notBlock' | 'empty' };

/**
 * Copies the selection: one cell, or a rectangular block. Cells picked
 * separately with Ctrl/⌘+click can't be copied together, as in a
 * spreadsheet. Only cells with a crop are copied.
 */
export const copyRotationSelection = (
  positions: GridPosition[],
  rows: RotationGridRow[],
  seasons: number[]
): RotationCopyResult => {
  if (positions.length === 0) return { error: 'empty' };
  const top = Math.min(...positions.map((p) => p.row));
  const left = Math.min(...positions.map((p) => p.col));
  const height = Math.max(...positions.map((p) => p.row)) - top + 1;
  const width = Math.max(...positions.map((p) => p.col)) - left + 1;
  if (positions.length !== width * height) return { error: 'notBlock' };

  const entries: RotationClipboardEntry[] = [];
  const sourceKeys: string[] = [];
  positions.forEach((position) => {
    const found = cellAt(rows, seasons, position);
    if (!found?.cell?.cropName) return;
    entries.push({
      dRow: position.row - top,
      dCol: position.col - left,
      values: toValues(found.cell),
    });
    sourceKeys.push(found.key);
  });
  if (entries.length === 0) return { error: 'empty' };

  const single = width === 1 && height === 1;
  const { values } = entries[0];
  return {
    clipboard: {
      sourceKeys,
      label: single
        ? [values.crop.cropName, values.variety?.varietyName]
            .filter(Boolean)
            .join(' · ')
        : ROTATION_COPIED_COUNT(entries.length),
      width,
      height,
      entries,
    },
  };
};

/** True when the values carry any sowing detail. */
export const valuesHaveSowing = ({ sowing }: RotationCellValues) =>
  (sowing.mix !== null && sowing.mix !== undefined) ||
  (sowing.rate !== null && sowing.rate !== undefined) ||
  (sowing.cost !== null && sowing.cost !== undefined) ||
  (sowing.date !== null && sowing.date !== undefined);

/**
 * The copied values as written into a cell in `season`: the sowing date
 * moves to the same day in that year, so it stays inside the cell's season
 * (29 February becomes 1 March in a common year).
 */
export const valuesForSeason = (
  values: RotationCellValues,
  season: number
): RotationCellValues => {
  const { date } = values.sowing;
  if (!date) return values;
  const moved = dayjs(date.slice(0, 10)).year(season);
  return {
    ...values,
    sowing: {
      ...values.sowing,
      date: `${moved.format('YYYY-MM-DD')}T00:00:00.000Z`,
    },
  };
};

interface PlanRotationPasteArgs {
  clipboard: RotationClipboard;
  /** The selection pasted into. A block uses only its top-left corner. */
  positions: GridPosition[];
  rows: RotationGridRow[];
  seasons: number[];
  lockReasonFor: (cell: RotationCell) => TipText | null;
}

/**
 * Which cells a paste writes to, and which it skips and why (OP-2501). A
 * paste overwrites a cell's crop, variety and Plan sowing details; it
 * creates a plan in an empty cell. A seed mix is copied and pasted whole.
 * It never touches a paddock with no zone, a plan open or unsaved in
 * Planning, a plan with an Actual sowing record or more than one crop
 * establishment task, or an empty second-crop row. Pasting a cell onto
 * itself does nothing.
 */
export const planRotationPaste = ({
  clipboard,
  positions,
  rows,
  seasons,
  lockReasonFor,
}: PlanRotationPasteArgs): RotationPastePlan => {
  const isSingle = clipboard.width === 1 && clipboard.height === 1;
  let placements: { position: GridPosition; values: RotationCellValues }[];
  if (isSingle) {
    placements = positions.map((position) => ({
      position,
      values: clipboard.entries[0].values,
    }));
  } else if (positions.length === 0) {
    placements = [];
  } else {
    const anchor = {
      row: Math.min(...positions.map((p) => p.row)),
      col: Math.min(...positions.map((p) => p.col)),
    };
    placements = clipboard.entries.map((entry) => ({
      position: { row: anchor.row + entry.dRow, col: anchor.col + entry.dCol },
      values: entry.values,
    }));
  }

  const targets: RotationPasteTarget[] = [];
  const skipped: RotationPasteSkip[] = [];
  const seen = new Set<string>();
  const sources = new Set(clipboard.sourceKeys);
  let outside = 0;

  placements.forEach(({ position, values }) => {
    const found = cellAt(rows, seasons, position);
    if (!found) {
      outside += 1;
      return;
    }
    const { gridRow, season, key, cell } = found;
    const { paddock, lane } = gridRow;
    if (sources.has(key) || seen.has(key)) return;
    seen.add(key);

    const skip = (reason: string) =>
      skipped.push({
        key,
        position,
        paddockName: paddock.paddockName,
        season,
        reason,
      });

    // A single copied cell only reaches selectable cells; a block can land
    // on an empty second-crop row.
    if (!cell && lane > 0) {
      skip(ROTATION_PASTE_SKIP_SECOND_ROW);
      return;
    }
    if (!paddock.zoneId) {
      skip(ROTATION_PASTE_SKIP_NO_ZONE);
      return;
    }
    if (cell) {
      if (lockReasonFor(cell)) {
        skip(ROTATION_PASTE_SKIP_LOCKED);
        return;
      }
      if (cell.hasActualSowing) {
        skip(ROTATION_PASTE_SKIP_ACTUAL_SOWING);
        return;
      }
      if (cell.sowingState === 'multiple') {
        skip(ROTATION_PASTE_SKIP_MULTIPLE_SOWING);
        return;
      }
    }
    targets.push({
      key,
      position,
      paddockId: paddock.paddockId,
      paddockName: paddock.paddockName,
      season,
      lane,
      inputId: cell?.inputId ?? null,
      hasOtherTasks: cell?.hasOtherTasks ?? false,
      replacesMix: !!cell?.sowing?.mix && !values.sowing.mix,
      values,
    });
  });

  return {
    clipboard,
    targets,
    skipped,
    outside,
    tooMany: targets.length > ROTATION_PASTE_MAX_CELLS,
  };
};

/**
 * How a paste target will look once applied — the cell as it is with the
 * pasted crop, variety and Plan sowing laid over it (or a new plan's cell),
 * so the grid can show the paste as a draft before anything is saved.
 */
export const draftRotationCell = (
  cell: RotationCell | null,
  values: RotationCellValues,
  season: number
): RotationCell => {
  const { sowing } = valuesForSeason(values, season);
  const hasSowing = valuesHaveSowing(values);
  const draftSowing = hasSowing
    ? {
        taskId: '',
        stepId: null,
        rate: sowing.mix ? null : sowing.rate,
        unit: sowing.mix ? null : sowing.unit,
        cost: sowing.mix ? null : sowing.cost,
        date: sowing.date,
        dateHasDay: sowing.date !== null && sowing.date !== undefined,
        mix: sowing.mix,
      }
    : null;
  return {
    inputId: cell?.inputId ?? '',
    season,
    cropName: values.crop.cropName,
    cropId: values.crop.globalId ?? null,
    varietyName: values.variety?.varietyName ?? null,
    varietyId: values.variety?.globalId ?? null,
    areaHa: cell?.areaHa ?? null,
    areaValue: cell?.areaValue ?? null,
    areaUnit: cell?.areaUnit ?? null,
    hasActualSowing: false,
    hasOtherTasks: cell?.hasOtherTasks ?? false,
    hasActualTasks: cell?.hasActualTasks ?? false,
    sowingState: 'single',
    sowing: draftSowing,
    sowingMismatch: null,
    // A paste only ever writes Plan, so the draft shows Plan.
    shownCategory: InputTaskCategory.Plan,
    shownSowingState: 'single',
    shownSowing: draftSowing,
    yieldTPerHa: null,
    productionT: null,
  };
};

/** Every cell in the rectangle between two positions, row by row. */
export const positionsBetween = (
  a: GridPosition,
  b: GridPosition
): GridPosition[] => {
  const positions: GridPosition[] = [];
  for (let row = Math.min(a.row, b.row); row <= Math.max(a.row, b.row); row += 1) {
    for (let col = Math.min(a.col, b.col); col <= Math.max(a.col, b.col); col += 1) {
      positions.push({ row, col });
    }
  }
  return positions;
};
